// This file is for helpers that relate to any api fetching within the application

import * as R from 'ramda';
import qs from 'qs';
import { format, parseISO } from 'date-fns';

export const setErrorWithoutSnack = (error, dispatch, fn) => {
  if (error.response) {
    dispatch(
      fn({
        error: error.response.data.title,
      })
    );
  }
};

export const getStringifiedQueryParams = R.partialRight(qs.stringify, [
  {
    allowDots: true,
    encode: false,
    filter: (prefix, value) =>
      prefix === 'quickFilter' && typeof value === 'string' ? encodeURIComponent(value) : value,
  },
]);

export const generateSearchParams = (params, affiliates) => {
  const {
    fromDate,
    toDate,
    status: Status,
    originator: OriginatorIdName,
    recipient: RecipientIdName,
    location: LocationId,
    ptoNumber: PtoNumber,
    referenceId: ReferenceId,
    product: ProductId,
  } = params;

  return {
    FromDate: format(parseISO(fromDate), 'yyyy-MM-dd'),
    LocationId,
    OriginatorIdName,
    ProductId,
    PtoNumber,
    RecipientIdName,
    ReferenceId,
    Status,
    ToDate: format(parseISO(toDate), 'yyyy-MM-dd'),
    ...affiliates,
  };
};

// generateListTags :: (Tag, String) -> [a] -> [b]
export const generateListTags = R.curry((type, idPath, list) =>
  list
    ? [...list.map(item => ({ id: R.path(idPath.split('.'), item), type })), { id: 'LIST', type }]
    : [{ id: 'LIST', type }]
);

// generateTag :: (Tag, String) -> a -> [b]
export const generateTag = R.curry((type, idPath, result) =>
  result ? [{ id: R.path(idPath.split('.'), result), type }] : []
);
/**
 * Creates an onQueryStarted handler that updates cached query data without triggering a refetch.
 * Useful for optimistic or pessimistic updates to list data in RTK Query.
 *
 * @param {Object} api - The RTK Query API instance (e.g., loadingManagementApi)
 * @param {string} endpointName - The name of the query endpoint to update
 * @param {Function} updateFn - Function that receives (draft, mutationArgs) and updates the draft
 * @param {Object} options - Optional configuration
 * @param {string[]} options.invalidateOnError - Tag types to invalidate if the mutation fails
 * @returns {Function} onQueryStarted handler for RTK Query mutations
 *
 * @example
 * // Single row update:
 * toggleItem: mutation({
 *   onQueryStarted: createCacheUpdateHandler(
 *     myApi,
 *     'getItemsList',
 *     (draft, { body, isActive }) => {
 *       const item = draft.items?.find(i => i.id === body.id);
 *       if (item) item.active = !isActive;
 *     },
 *     { invalidateOnError: ['ItemList'] }
 *   ),
 *   query: ({ body }) => ({ ... })
 * })
 *
 * @example
 * // Bulk row update:
 * bulkUpdateStatus: mutation({
 *   onQueryStarted: createCacheUpdateHandler(
 *     myApi,
 *     'getItemsList',
 *     (draft, { itemIds, newStatus }) => {
 *       itemIds.forEach(id => {
 *         const item = draft.items?.find(i => i.id === id);
 *         if (item) item.status = newStatus;
 *       });
 *     },
 *     { invalidateOnError: ['ItemList'] }
 *   ),
 *   query: ({ itemIds, newStatus }) => ({ ... })
 * })
 */
export const createCacheUpdateHandler =
  (api, endpointName, updateFn, options = {}) =>
  async (mutationArgs, { dispatch, getState, queryFulfilled }) => {
    try {
      // Wait for the mutation to complete (pessimistic update)
      await queryFulfilled;

      // Get all cached queries from the API state
      const state = getState();
      const apiState = state[api.reducerPath];

      // Filter for matching queries
      const matchingQueries = Object.values(apiState.queries).filter(
        queryInfo => queryInfo?.endpointName === endpointName && queryInfo?.status === 'fulfilled'
      );

      // Update each matching cached query
      matchingQueries.forEach(queryInfo => {
        dispatch(
          api.util.updateQueryData(endpointName, queryInfo.originalArgs, draft => {
            updateFn(draft, mutationArgs);
          })
        );
      });
    } catch (error) {
      // On error, optionally invalidate tags to trigger a refetch
      if (options.invalidateOnError) {
        dispatch(api.util.invalidateTags(options.invalidateOnError));
      }
    }
  };
